<script setup lang="ts">
import { computed } from "vue";

import AddAttachmentButton from "~/components/dumb/AddAttachmentButton.vue";
import AttachmentImage from "~/components/dumb/AttachmentImage.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { CommandId, EditorMode, ViewKind } from "~/shared/enums";
import type { Attachment } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useUserStore } from "~/stores";
import { checkFileSize } from "~/utils/api";
import { makeStringComparator } from "~/utils/comparator";

import DragArea from "./DragArea.vue";

const props = defineProps<{
  attachments: Attachment[];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const userStore = useUserStore();

const emit = defineEmits<{
  add: [value: File[]];
  remove: [attachment: Attachment];
}>();

const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);
const hasAccess = computed(() => userStore.hasAccessToImportExport());

const onFileAdd = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files) {
    return;
  }
  const filteredFiles = Array.from(input.files).filter((file) => checkFileSize(file));

  emit("add", filteredFiles);
  input.value = "";
};
const items = computed(() =>
  props.attachments
    .map((attachment) => ({
      ...attachment,
      title: attachment.name,
      undraggable: isTrash.value,
    }))
    .sort(makeStringComparator((e) => e.order))
);

const getComponentProps = (attachment: Attachment) => ({
  attachment,
  editorMode: EditorMode.DETAIL,
  resizable: false,
  widthPx: null,
  onRemove: () => emit("remove", attachment),
});

const moveAttachment = (_: string, attachment: Attachment) => {
  const { duid, order } = attachment;
  dataStore.updateAttachment({ duid, order });
};
</script>

<template>
  <AddAttachmentButton v-if="pageStore.isPublicForm && items.length === 0" class="ml-1" @input="onFileAdd" />
  <DragArea
    v-else
    group="attachment-editor"
    category="attachments"
    horizontal
    class="!h-auto w-full gap-2 py-1"
    :class="pageStore.isPublicForm || appStore.formInInternalModal ? 'px-1' : 'px-5'"
    :items="items"
    :component="AttachmentImage"
    :get-component-props="getComponentProps"
    @change="moveAttachment">
    <template #after>
      <Tooltip v-if="!pageStore.isPublicView && !isTrash && hasAccess" :command-id="CommandId.ADD_ATTACHMENT">
        <AddAttachmentButton @input="onFileAdd" />
      </Tooltip>
    </template>
  </DragArea>
</template>
